/* istanbul ignore file */

import type { RefObject } from 'react';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useSelector } from '@xstate/react';
import { convertDistance, getDistance } from 'geolib';
import { type InterpreterFrom } from 'xstate';
import {
  Button,
  LoadingPlaceholder,
  theme,
  usePressableState,
  useResponsive,
  useToggleState,
  webOnlyStyles,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { getLocationOrderChannel } from '../../../../helpers';
import { useNewLocationsLayout, usePredictions } from '../../../../hooks';
import { type createLocationSearchMachine } from '../../../../machine';
import { type Location, type PositionCoordinates } from '../../../../machine';
import { useLocationResultsFeeDisclosure } from '../../hooks';
import {
  LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_SM,
  LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
  LOCATION_RESULTS_CARD_HEIGHT_WITH_DISCLAIMER,
  LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING,
  LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING_SM,
  LOCATION_RESULTS_CARD_HEIGHT_XS,
  LOCATION_RESULTS_CARD_MIN_HEIGHT_SM,
} from '../../LocationResults.constants';
import type { OnSelectedLocationCardPress } from './LocationResultCard.types';
import {
  LocationResultCardDetailsModal,
  LocationResultCardDistanceModal,
  LocationResultCardFeeDisclosure,
} from './subcomponents';
import {
  LocationResultCardContent,
  LocationResultCardContentV2,
} from './variations';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsCard = memo((props: LocationResultsCardProps) => {
  const {
    locationSearchService,
    location,
    withBottomBorder,
    withDetailsModal,
    withoutOrderNowBtn,
    shouldShowLocationOrderChannel = false,
    isLoading = false,
    isDisabled = false,
    onPress,
    onLayout,
    onFocus,
    onUmount,
  } = props;

  const {
    id,
    name,
    address,
    city,
    state,
    zipCode,
    imageUrl,
    estimatedDeliveryTime,
    restaurantSlug,
    isClosed,
    acceptingOrders = true,
    lat: latitude,
    lng: longitude,
    isDelivery,
  } = location;

  const { formatMessage } = useIntl();

  const { isSurfaceDistanceForPredictionEnabled } = usePredictions({
    locationSearchService,
  });

  const locationOrderChannel = getLocationOrderChannel(location);
  const isDeliveryLocation = locationOrderChannel === 'delivery';

  // ─── State ───────────────────────────────────────────────────────────

  const { value: isDistanceModalVisible, toggle: toggleDistanceModal } =
    useToggleState();

  // ─── Derived Data ────────────────────────────────────────────────────

  const currentPosition = useSelector(
    locationSearchService,
    (snapshot) => snapshot.context.currentPosition,
  );
  const baseLocation = useSelector(
    locationSearchService,
    (snapshot) => snapshot.context.baseLocation,
  );

  const locationDistance = useMemo(() => {
    const isFeatureEnabled = isSurfaceDistanceForPredictionEnabled;
    const hasRequiredCoordinates = Boolean(latitude && longitude);

    if (!isFeatureEnabled || !hasRequiredCoordinates) return;

    const locationToCalculateDistanceFrom =
      isSurfaceDistanceForPredictionEnabled
        ? (currentPosition ?? baseLocation)
        : currentPosition;

    if (!locationToCalculateDistanceFrom) return;

    return getLocationDistance(locationToCalculateDistanceFrom, {
      latitude,
      longitude,
    });
  }, [
    isSurfaceDistanceForPredictionEnabled,
    latitude,
    longitude,
    currentPosition,
    baseLocation,
  ]);

  const shouldSurfaceDistance =
    isSurfaceDistanceForPredictionEnabled &&
    typeof locationDistance?.formatted === 'string';

  const formattedAddress = useMemo(() => {
    if (!shouldSurfaceDistance) {
      return address;
    }

    const formattedDistance = formatMessage(messages.distance, {
      distance: locationDistance?.formatted,
    });

    return `${formattedDistance} · ${address}`;
  }, [address, formatMessage, locationDistance, shouldSurfaceDistance]);

  // ─── Flags ───────────────────────────────────────────────────────────

  const tooFarLocationThresholdInMiles = useFeatureFlag('CELS-4212-too-far-location-threshold-in-miles'); // prettier-ignore
  const locationDistanceInMiles = locationDistance?.raw ?? 0;
  const isTooFarLocationModalEnabled =
    tooFarLocationThresholdInMiles > 0 &&
    locationDistanceInMiles > tooFarLocationThresholdInMiles;

  // ─────────────────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const isVisible = useIsFocused();
  const { match, minWidth } = useResponsive();

  // ─── State ───────────────────────────────────────────────────────────

  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);

  const cardRef = useRef<View>(null);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const toggleDetailsModal = useCallback(() => {
    setIsDetailsModalVisible((currentValue) => !currentValue);
  }, []);

  const handleOnLayout = useCallback(() => {
    onLayout?.(cardRef, id);
  }, [id, onLayout]);

  const handleOnPress = useCallback(() => {
    const entryPoint = isDeliveryLocation ? 'delivery-cta' : 'card';

    onPress(
      { restaurantId: id, restaurantSlug, restaurantName: name },
      { entryPoint },
    );
  }, [isDeliveryLocation, onPress, id, restaurantSlug, name]);

  const handleOnFocus = useCallback(() => {
    onFocus?.(id);
  }, [id, onFocus]);

  const handleOnUnmount = useCallback(() => {
    onUmount?.(id);
  }, [id, onUmount]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => handleOnUnmount, [handleOnUnmount]);

  // ─────────────────────────────────────────────────────────────────────

  const shouldUseNewLocationsLayout = useNewLocationsLayout();
  const shouldDisplayDetailsModal = Boolean(
    withDetailsModal && isDetailsModalVisible && isVisible,
  );
  const shouldDisableControls = isDisabled || isLoading;
  const shouldDisplayClosedNotice =
    locationOrderChannel !== 'delivery' && !acceptingOrders;
  const shouldDisplayETA = estimatedDeliveryTime !== undefined;
  const shouldDisplayAddressDetails = locationOrderChannel !== 'delivery';
  const shouldDisplayOrderCTA =
    minWidth.isSM && isDeliveryLocation && !withoutOrderNowBtn;

  // ─────────────────────────────────────────────────────────────────────

  const ctaLabel = isClosed
    ? t('location.results.card.order-later')
    : t('location.results.card.order-now');

  const hasAddressDetails = Boolean(city && state && zipCode);
  const addressDetails = hasAddressDetails
    ? `${city}, ${state} ${zipCode}`
    : undefined;
  const locationAddressDetails = shouldDisplayAddressDetails
    ? addressDetails
    : undefined;

  const locationResultA11yName = isDeliveryLocation
    ? t('general.delivery')
    : name;

  // ─── Fee Disclaimer ──────────────────────────────────────────────────

  const { shouldShowDeliveryFeeDisclaimer, shouldShowOutpostFeeDisclaimer } =
    useLocationResultsFeeDisclosure({ location });
  const shouldShowFeeDisclaimer =
    shouldShowDeliveryFeeDisclaimer || shouldShowOutpostFeeDisclaimer;

  // ─── Styles ──────────────────────────────────────────────────────────

  const { isHovered, isFocused } = usePressableState(cardRef);

  const cardStyle = [
    shouldUseNewLocationsLayout
      ? match([styles.cardV2XS, styles.cardV2SM])
      : match([
          shouldShowFeeDisclaimer ? styles.cardXSWithDisclaimer : styles.cardXS,
          styles.cardSM,
        ]),
    !shouldUseNewLocationsLayout && withBottomBorder
      ? styles.cardWithBorder
      : undefined,
    isDisabled ? styles.cardDisabled : undefined,
    !shouldDisableControls && (isHovered || isFocused)
      ? styles.cardHover
      : undefined,
    isFocused ? webOnlyStyles({ outlineStyle: 'dotted' }) : undefined,
    webOnlyStyles({
      outlineColor: theme.colors.GRAY,
      outlineOffset: theme.spacing['1'],
      outlineWidth: 2,
      transition: `opacity ${theme.transitions.base}ms`,
    }),
  ];
  const feeDisclosureContainerStyles = isDeliveryLocation
    ? match([undefined, styles.deliveryFeeDisclaimerContainerSM])
    : undefined;

  const a11yLabel = shouldSurfaceDistance
    ? formatMessage(messages.a11yLabelWithDistance, { restaurant: locationResultA11yName, distance: locationDistance?.formatted }) // prettier-ignore
    : formatMessage(messages.a11yLabel, { restaurant: locationResultA11yName });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <TouchableOpacity
        ref={cardRef}
        style={cardStyle}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        disabled={shouldDisableControls}
        onLayout={handleOnLayout}
        onPress={
          isTooFarLocationModalEnabled ? toggleDistanceModal : handleOnPress
        }
        onFocus={onFocus ? handleOnFocus : undefined}
        accessibilityState={{ disabled: isDisabled, busy: isLoading }}
      >
        {shouldUseNewLocationsLayout ? (
          <LocationResultCardContentV2
            locationName={name}
            locationOrderChannel={locationOrderChannel}
            address={formattedAddress}
            addressDetails={locationAddressDetails}
            estimatedDeliveryTime={
              shouldDisplayETA ? estimatedDeliveryTime : undefined
            }
            isLoading={isLoading}
            withLocationOrderChannel={shouldShowLocationOrderChannel}
            withDetailsModal={withDetailsModal}
            toggleDetailsModal={toggleDetailsModal}
            shouldShowDeliveryFeeDisclaimer={shouldShowDeliveryFeeDisclaimer}
            shouldShowOutpostFeeDisclaimer={shouldShowOutpostFeeDisclaimer}
            shouldDisableControls={shouldDisableControls}
            shouldDisplayClosedNotice={shouldDisplayClosedNotice}
          />
        ) : (
          <LocationResultCardContent
            locationName={name}
            locationOrderChannel={locationOrderChannel}
            imageUrl={imageUrl}
            address={address}
            addressDetails={locationAddressDetails}
            estimatedDeliveryTime={
              shouldDisplayETA ? estimatedDeliveryTime : undefined
            }
            withDetailsModal={withDetailsModal}
            toggleDetailsModal={toggleDetailsModal}
            isLoading={isLoading}
            shouldShowDeliveryFeeDisclaimer={shouldShowDeliveryFeeDisclaimer}
            shouldShowOutpostFeeDisclaimer={shouldShowOutpostFeeDisclaimer}
            shouldDisableControls={shouldDisableControls}
            shouldDisplayClosedNotice={shouldDisplayClosedNotice}
          />
        )}
      </TouchableOpacity>

      {shouldDisplayOrderCTA ? (
        <View style={styles.ctaContainer}>
          <Button
            size="large"
            isLoading={isLoading}
            onPress={
              isTooFarLocationModalEnabled ? toggleDistanceModal : handleOnPress
            }
          >
            {ctaLabel}
          </Button>
        </View>
      ) : null}

      {/* ─── Fee Disclosure ─────────────────────────────────────────────── */}

      {shouldUseNewLocationsLayout ? null : (
        <>
          {shouldShowDeliveryFeeDisclaimer && minWidth.isSM ? (
            <View style={feeDisclosureContainerStyles}>
              <LocationResultCardFeeDisclosure.Delivery />
            </View>
          ) : null}

          {shouldShowOutpostFeeDisclaimer && minWidth.isSM ? (
            <LocationResultCardFeeDisclosure.Outpost />
          ) : null}
        </>
      )}

      {/* ─── Details Modal ──────────────────────────────────────────────── */}

      <LocationResultCardDetailsModal
        visible={shouldDisplayDetailsModal}
        location={location}
        locationOrderChannel={locationOrderChannel}
        addressDetails={addressDetails}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onRequestClose={toggleDetailsModal}
        onOrderNowBtnPress={onPress}
      />

      {/* ─── Distance Modal ─────────────────────────────────────────────── */}

      <LocationResultCardDistanceModal
        isVisible={isDistanceModalVisible}
        location={isDelivery ? name || address : name}
        onRequestClose={toggleDistanceModal}
        onContinue={handleOnPress}
      />
    </>
  );
});

// ─── Variations ──────────────────────────────────────────────────────────────

export const LocationResultCardLoading = memo(
  (props: Pick<LocationResultsCardProps, 'withBottomBorder'>) => {
    const { withBottomBorder } = props;

    const { match } = useResponsive();

    // ─── Flags ───────────────────────────────────────────────────────────

    const shouldUseNewLocationsLayout = useNewLocationsLayout();

    // ─── Styles ──────────────────────────────────────────────────────────

    const cardStyle = [
      match([styles.cardXS, styles.cardSM]),
      withBottomBorder ? styles.cardWithBorder : undefined,
    ];
    const cardV2Style = match([styles.cardV2XS, styles.cardV2SM]);

    // ─────────────────────────────────────────────────────────────────────

    if (shouldUseNewLocationsLayout) {
      return (
        <View style={cardV2Style}>
          <LoadingPlaceholder
            rows={1}
            columns={1}
            rowHeight={100}
            borderRadius={theme.spacing['2']}
            palette="cream"
          />
        </View>
      );
    }

    return (
      <View style={cardStyle}>
        <LoadingPlaceholder
          rows={1}
          columns={1}
          rowHeight={match([
            LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING,
            LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING_SM,
          ])}
          borderRadius={theme.spacing['2']}
          palette="cream"
        />
      </View>
    );
  },
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Location result for {restaurant}',
    description: 'Location results card | a11y Label',
  },
  a11yLabelWithDistance: {
    defaultMessage: 'Location result for {restaurant}, {distance} miles away',
    description: 'Location results card | a11y Label',
  },
  distance: {
    defaultMessage: '{distance} miles away',
    description: 'Location results card | Distance',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardXS: {
    height: LOCATION_RESULTS_CARD_HEIGHT_XS,
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
  },
  cardXSWithDisclaimer: {
    height: LOCATION_RESULTS_CARD_HEIGHT_WITH_DISCLAIMER,
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
  },
  cardSM: {
    minHeight: LOCATION_RESULTS_CARD_MIN_HEIGHT_SM,
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_SM,
  },
  cardV2XS: {
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
  },
  cardV2SM: {
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_SM,
  },
  cardWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LIGHT,
  },
  cardHover: {
    opacity: 0.7,
  },
  cardDisabled: {
    opacity: 0.6,
  },
  ctaContainer: {
    paddingTop: theme.spacing['1'],
  },
  deliveryFeeDisclaimerContainerSM: {
    marginTop: theme.spacing['4'],
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns location distance (in miles) from the current position.
 */
function getLocationDistance(
  currentPosition: PositionCoordinates,
  locationPosition: PositionCoordinates,
) {
  const distance = getDistance(currentPosition, locationPosition);
  const distanceInMiles = convertDistance(distance, 'mi');

  return { formatted: distanceInMiles.toFixed(2), raw: distanceInMiles };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsCardProps = {
  locationSearchService: InterpreterFrom<ReturnType<typeof createLocationSearchMachine>>; // prettier-ignore
  location: Location;
  withBottomBorder?: boolean;
  withDetailsModal?: boolean;
  withoutOrderNowBtn?: boolean;
  shouldShowLocationOrderChannel?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  onLayout?: (listItemRef: RefObject<View>, listItemId: string) => void;
  onPress: OnSelectedLocationCardPress;
  onFocus?: (id: string | undefined) => void;
  onUmount?: (id: Location['id']) => void;
};
